import { Injectable } from '@angular/core';
import { INoticeSnackBarOptions, INoticeDialogOptions, INoticeIconSnackBarOptions, INotifyOptions } from './models/interfaces';
import { NotofierLegacyService } from './services/notofier-legacy.service';
import { NotofierStrategyService } from './services/notofier-strategy.service';
import { NotofierTemplatingService } from './services/notofier-templating.service';
import { INotificationPriority, INotificationService } from '../error-handler/error-handler-interfaces.service';
/** A service for showing various types of notifications via Material style notifications, such as snackbars and dialogs. */
@Injectable({ providedIn: 'root' })
export class NotofierService implements INotificationService {
    // Legacy facades TODO: Maybe these can work the same as the show methods.
    public show;
    public showError;

    /** Not yet used. */
    // private notificationVisible = false;

    constructor (
        private _strategies: NotofierStrategyService,
        private _legacy: NotofierLegacyService,
        private _templating: NotofierTemplatingService
    ) {
        this.show = this._legacy.show;
        this.showError = this._legacy.showError;
    }

    // Convenience methods
    /** Display a snackbar notification */
    showSnackbar (options: INoticeSnackBarOptions) {
        return this._templating.openMaterialSnackbar.call(this._templating, options);
    }
    /** Display a snackbar notification with a configurable icon. */
    showIconSnackbar (options: INoticeIconSnackBarOptions) {
        return this._templating.openIconSnackbarFromTemplate.call(this._templating, options);
    }
    /** Display a configurable dialog. */
    showDialog (options: INoticeDialogOptions) {
        return this._templating.openDialogFromTemplate.call(this._templating, options);
    }

    /**
	 * A method for displaying a notification based on priority level. Most true notifications should use this function
	 * instead of the lower level functions (e.g. `showInfoNotification()`.)
	 * @arg message The message that will be displayed in the notification.
	 * @arg priority The importance of the message. The type of notification that appears is based on it's priority.
	 * @arg options Options to customize the notification.
	 * If the notification is a Material component, you can add the appropriate configuration properties here.
	 */
    notify = (message: string, priority: INotificationPriority | number = 0, options: INotifyOptions = {}) => {
        const _priority = this.normalizePriorityLevel(priority);

        // In each case we should actually probably be pushing these on to the queue.
        if (_priority < 0) {
            // Silent
        } else if (_priority < 1) {
            // Debug
            this._strategies.showDebugNotification(message, options);
        } else if (_priority <= 20) {
            /* Show self closing snackbar */
            this._strategies.showInfoNotification(message, options);
        } else if (_priority <= 40) {
            /* Show snackbar with an action */
            this._strategies.showMinorNotification(message, options);
        } else if (_priority <= 80) {
            /* Show snackbar with an action */
            this._strategies.showModerateNotification(message, options);
        } else if (_priority === 100) {
            /* Show critical error dialog */
            this._strategies.showCriticalNotification(message, options);
        } else {
            console.log('Unable to determine notification priority');
            this._strategies.showInfoNotification(message, options);
        }
    };

    /** Takes a value, including our keywords, and transforms it in to an numeric notification level between 1 and 100. */
    normalizePriorityLevel (priorityLevel: INotificationPriority | number = 1): number {
        let _priority = priorityLevel;
        const priorityLookup = {
            silent: -1,
            debug: 0,
            info: 20,
            minor: 40,
            moderate: 60,
            major: 80,
            critical: 100
        };
        // Return the matching value from the lookup array, or check the number's bounds and return it.
        if (typeof _priority !== 'number') {
            _priority = priorityLookup[_priority] || 0;
        } else {
            _priority = Math.floor(_priority);
            if (_priority > 100) {
                _priority = 100;
            }
            if (_priority < -1) {
                _priority = -1;
            }
        }
        return _priority as number;
    }
}
